import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EventNames, TrackingEventTypes, useAnalytics } from "@cinch-nx/shared-analytics";
import cx from "classnames";
import Link from "next/link";
import Head from "next/head";
import { usePagination } from "./hooks";
import styles from "./pagination.module.css";
const ellipses = "...";
// eslint-disable-next-line @typescript-eslint/no-empty-function
const noOp = function() {
// this does nothing.
};
const Pagination = ({ activePage , itemsCountPerPage , totalItemsCount , sourcePage , onChange =noOp , enabledLinks =false , includedQueryParams =""  })=>{
    // Guessing this is so we can wait for API calls to resolve
    // However this is maybe irrelevant if we're server-side fetching
    // So lets revisit this once the API is hooked up
    const currentPage = activePage || 1;
    const itemsPerPage = itemsCountPerPage || 24;
    const { items  } = usePagination({
        currentPage,
        itemsPerPage,
        totalItemsCount
    });
    const { trackEvent  } = useAnalytics();
    const handleClick = ()=>{
        trackEvent({
            eventName: `${sourcePage} - Pagination click`,
            type: TrackingEventTypes.ADOBE,
            data: {
                event: {
                    name: EventNames.click,
                    category: "page bottom",
                    label: "pagination",
                    action: EventNames.click
                }
            }
        });
    };
    const isFirstPage = currentPage === 1;
    const isLastPage = currentPage === items[items.length - 1] || items.length === 0;
    const isMultipleEllipses = items.filter((item)=>item === ellipses).length > 1;
    const renderPrevNextButtons = (direction, isDisabled)=>{
        const isNext = direction === "next";
        const ariaLabel = isNext ? "Next page" : "Previous page";
        const pageNumber = isNext ? currentPage + 1 : currentPage - 1;
        const pageHref = pageNumber === 1 ? `/${sourcePage}` : `/${sourcePage}/page/${pageNumber}`;
        return enabledLinks ? /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsxs(Head, {
                    children: [
                        !isFirstPage && direction === "previous" ? /*#__PURE__*/ _jsx("link", {
                            rel: "prev",
                            href: pageHref
                        }, `next-link-prev`) : null,
                        !isLastPage && direction === "next" ? /*#__PURE__*/ _jsx("link", {
                            rel: "next",
                            href: pageHref
                        }, `next-link-next`) : null
                    ]
                }),
                /*#__PURE__*/ _jsx(Link, {
                    href: isDisabled ? "" : `${pageHref}${includedQueryParams}`,
                    passHref: true,
                    legacyBehavior: true,
                    children: /*#__PURE__*/ _jsx("a", {
                        "aria-disabled": isDisabled,
                        href: pageHref,
                        "aria-label": ariaLabel,
                        onClick: (e)=>!isDisabled ? onChange(pageNumber) : e.preventDefault(),
                        className: cx(styles.button, styles.nextPrevButton),
                        children: isNext ? "Next" : "Prev"
                    })
                })
            ]
        }) : /*#__PURE__*/ _jsx("button", {
            "aria-disabled": isDisabled,
            disabled: isDisabled,
            onClick: ()=>!isDisabled ? onChange(pageNumber) : null,
            "aria-label": ariaLabel,
            className: cx(styles.button, styles.nextPrevButton),
            children: isNext ? "Next" : "Prev"
        });
    };
    const renderPageNumberButton = (pageNumber)=>{
        const isLastItem = pageNumber === items[items.length - 1];
        const ariaLabel = isLastItem ? `Page ${pageNumber}, last page` : `Page ${pageNumber}`;
        const pageHref = pageNumber === 1 ? `/${sourcePage}` : `/${sourcePage}/page/${pageNumber}`;
        return enabledLinks ? /*#__PURE__*/ _jsx(Link, {
            href: `${pageHref}${includedQueryParams}`,
            passHref: true,
            legacyBehavior: true,
            children: /*#__PURE__*/ _jsx("a", {
                href: pageHref,
                "aria-label": ariaLabel,
                "aria-current": activePage === pageNumber ? "page" : undefined,
                "data-testid": "page-number-button",
                className: cx(styles.button, styles.pageNumberButton, {
                    [styles.pageNumberButtonActive]: activePage === pageNumber
                }),
                onClick: (e)=>onChange(pageNumber),
                children: pageNumber.toLocaleString()
            })
        }) : /*#__PURE__*/ _jsx("button", {
            "aria-label": ariaLabel,
            "aria-current": activePage === pageNumber ? "page" : undefined,
            "data-testid": "page-number-button",
            onClick: ()=>onChange(pageNumber),
            className: cx(styles.button, styles.pageNumberButton, {
                [styles.pageNumberButtonActive]: activePage === pageNumber
            }),
            children: pageNumber.toLocaleString()
        });
    };
    return /*#__PURE__*/ _jsx("nav", {
        "aria-label": "Pagination",
        className: styles.pagination,
        "data-testid": "pagination",
        children: /*#__PURE__*/ _jsxs("ul", {
            onClick: handleClick,
            className: cx(styles.list, {
                [styles.listWithMultipleEllipses]: isMultipleEllipses
            }),
            children: [
                /*#__PURE__*/ _jsx("li", {
                    className: styles.listItem,
                    children: renderPrevNextButtons("previous", isFirstPage)
                }),
                items.length > 0 && items.map((pageNumber, index)=>pageNumber === ellipses ? /*#__PURE__*/ _jsx("li", {
                        className: cx(styles.listItem, styles.ellipses),
                        children: "…"
                    }, `${pageNumber}-${index}`) : /*#__PURE__*/ _jsx("li", {
                        className: styles.listItem,
                        children: renderPageNumberButton(Number(pageNumber))
                    }, pageNumber)),
                /*#__PURE__*/ _jsx("li", {
                    className: styles.listItem,
                    children: renderPrevNextButtons("next", isLastPage)
                })
            ]
        })
    });
};
export default Pagination;
